import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api';

const MESES = [
  '',
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

const fmtMonto = (n) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0
  }).format(n);
};

const badgeRun = (status) => {
  const map = {
    READY: 'bg-emerald-100 text-emerald-800',
    APPROVED: 'bg-teal-100 text-teal-800',
    SENT: 'bg-indigo-100 text-indigo-800',
    FAILED: 'bg-red-100 text-red-800',
    CALCULATING: 'bg-amber-100 text-amber-800',
    GENERATING: 'bg-blue-100 text-blue-800'
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const badgeFresh = (level) => {
  const map = {
    FRESH: 'text-emerald-700',
    STALE: 'text-amber-700',
    MISSING: 'text-red-700'
  };
  return map[level] || 'text-gray-600';
};

export default function PanelInformesIva() {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [anio, setAnio] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [configCliente, setConfigCliente] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [busyCliente, setBusyCliente] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/informes-iva/dashboard/${anio}/${mes}`);
      setDashboard(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [anio, mes]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const items = useMemo(() => {
    const list = dashboard?.items || [];
    const q = filtro.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (it) =>
        (it.cliente.nombre || '').toLowerCase().includes(q) ||
        (it.cliente.rut || '').toLowerCase().includes(q) ||
        (it.cliente.asignado || '').toLowerCase().includes(q)
    );
  }, [dashboard, filtro]);

  const abrirConfig = async (clienteId) => {
    try {
      const { data } = await api.get(`/informes-iva/config/cliente/${clienteId}`);
      setConfigCliente({ ...data, clienteId });
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const guardarConfig = async () => {
    if (!configCliente) return;
    try {
      await api.put(`/informes-iva/config/cliente/${configCliente.clienteId}`, {
        activoInformes: configCliente.activoInformes,
        emailCliente: configCliente.emailCliente,
        notaContador: configCliente.notaContador,
        remanenteManual: configCliente.remanenteManual,
        autoSendEnabled: configCliente.autoSendEnabled
      });
      toast.success('Configuración guardada');
      setConfigCliente(null);
      loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const verPreview = async (runId) => {
    try {
      const res = await api.get(`/informes-iva/preview/${runId}`, { responseType: 'text' });
      const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo abrir preview');
    }
  };

  const aprobar = async (runId) => {
    try {
      await api.post(`/informes-iva/approve/${runId}`);
      toast.success('Informe aprobado');
      await loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const enviar = async (runId) => {
    if (!window.confirm('¿Enviar informe al email del cliente?')) return;
    try {
      await api.post(`/informes-iva/send/${runId}`);
      toast.success('Informe enviado');
      await loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const syncSii = async (clienteId) => {
    setBusyCliente(clienteId);
    try {
      await api.post(`/informes-iva/sync/${clienteId}`, { mes, anio });
      toast.success('Sync RCV iniciado (fuentes módulo IVA)');
      await loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusyCliente(null);
    }
  };

  const calcular = async (clienteId) => {
    setBusyCliente(clienteId);
    try {
      const { data } = await api.post(`/informes-iva/run/${clienteId}`, { mes, anio, generatePdf: true });
      toast.success(`Informe listo (confianza ${data.confidence})`);
      await loadDashboard();
      if (data.runId) await verPreview(data.runId);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusyCliente(null);
    }
  };

  const descargarPdf = async (runId) => {
    try {
      const res = await api.get(`/informes-iva/pdf/${runId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe-iva-${runId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo descargar PDF');
    }
  };

  const cargarHistorial = async (clienteId) => {
    try {
      const { data } = await api.get(`/informes-iva/historial/${clienteId}`);
      setHistorial(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Informes IVA</h1>
          <p className="text-sm text-slate-600 mt-1">
            Clientes con IVA en listado Renta · Fase 1A (cálculo, preview y PDF, sin envío automático)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={mes}
            onChange={(e) => setMes(Number(e.target.value))}
          >
            {MESES.map((m, i) =>
              i === 0 ? null : (
                <option key={i} value={i}>
                  {m}
                </option>
              )
            )}
          </select>
          <input
            type="number"
            className="border rounded-lg px-3 py-2 text-sm w-24"
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
          />
          <button
            type="button"
            onClick={loadDashboard}
            className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm hover:bg-blue-800"
          >
            Actualizar
          </button>
        </div>
      </div>

      <div className="mb-4">
        <input
          type="search"
          placeholder="Buscar por nombre, RUT o asignado…"
          className="w-full max-w-md border rounded-lg px-3 py-2 text-sm"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
      </div>

      {loading && <p className="text-sm text-slate-500">Cargando dashboard…</p>}

      <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3 font-medium">Cliente</th>
              <th className="text-left p-3 font-medium">Asignado</th>
              <th className="text-left p-3 font-medium">Datos SII</th>
              <th className="text-left p-3 font-medium">Último run</th>
              <th className="text-right p-3 font-medium">IVA est.</th>
              <th className="text-right p-3 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const c = row.cliente;
              const run = row.lastRun;
              return (
                <tr key={c._id} className="border-t border-slate-100 hover:bg-slate-50/80">
                  <td className="p-3">
                    <div className="font-medium text-slate-900">{c.nombre}</div>
                    <div className="text-xs text-slate-500">{c.rut}</div>
                  </td>
                  <td className="p-3 text-slate-600">{c.asignado || '—'}</td>
                  <td className="p-3">
                    <span className={`text-xs font-medium ${badgeFresh(row.freshness?.overall)}`}>
                      {row.freshness?.overall || '—'}
                    </span>
                  </td>
                  <td className="p-3">
                    {run ? (
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${badgeRun(run.status)}`}>
                        {run.status} · {run.confidence || '—'}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs">Sin cálculo</span>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono text-slate-800">
                    {run?.estimatedVatDetermined != null ? fmtMonto(run.estimatedVatDetermined) : '—'}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap justify-end gap-1">
                      <button
                        type="button"
                        disabled={busyCliente === c._id}
                        onClick={() => calcular(c._id)}
                        className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {busyCliente === c._id ? 'Calculando…' : 'Calcular'}
                      </button>
                      <button
                        type="button"
                        disabled={busyCliente === c._id}
                        onClick={() => syncSii(c._id)}
                        className="px-2 py-1 text-xs border rounded hover:bg-slate-50 disabled:opacity-50"
                      >
                        Sync SII
                      </button>
                      {run?._id && ['READY', 'APPROVED', 'SENT'].includes(run.status) && (
                        <>
                          <button
                            type="button"
                            onClick={() => verPreview(run._id)}
                            className="px-2 py-1 text-xs border rounded hover:bg-slate-50"
                          >
                            HTML
                          </button>
                          <button
                            type="button"
                            onClick={() => descargarPdf(run._id)}
                            className="px-2 py-1 text-xs border rounded hover:bg-slate-50"
                          >
                            PDF
                          </button>
                          {run.status === 'READY' && (
                            <button
                              type="button"
                              onClick={() => aprobar(run._id)}
                              className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                            >
                              Aprobar
                            </button>
                          )}
                          {(run.status === 'APPROVED' || run.status === 'READY') && (
                            <button
                              type="button"
                              onClick={() => enviar(run._id)}
                              className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
                            >
                              Enviar
                            </button>
                          )}
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          abrirConfig(c._id);
                          cargarHistorial(c._id);
                        }}
                        className="px-2 py-1 text-xs border rounded hover:bg-slate-50"
                      >
                        Config
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && items.length === 0 && (
          <p className="p-6 text-center text-slate-500 text-sm">No hay clientes con IVA para este período.</p>
        )}
      </div>

      {configCliente && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">Configuración informe IVA</h2>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input
                type="checkbox"
                checked={Boolean(configCliente.activoInformes)}
                onChange={(e) => setConfigCliente({ ...configCliente, activoInformes: e.target.checked })}
              />
              Activo en informes
            </label>
            <label className="block text-sm mb-1">Email cliente</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={configCliente.emailCliente || ''}
              onChange={(e) => setConfigCliente({ ...configCliente, emailCliente: e.target.value })}
            />
            <label className="block text-sm mb-1">Remanente manual (504)</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={configCliente.remanenteManual ?? ''}
              onChange={(e) =>
                setConfigCliente({
                  ...configCliente,
                  remanenteManual: e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
            <label className="block text-sm mb-1">Nota del contador</label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4 min-h-[80px]"
              value={configCliente.notaContador || ''}
              onChange={(e) => setConfigCliente({ ...configCliente, notaContador: e.target.value })}
            />
            {historial.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium mb-2">Historial reciente</h3>
                <ul className="text-xs space-y-1 max-h-32 overflow-y-auto border rounded p-2">
                  {historial.map((h) => (
                    <li key={h._id} className="flex justify-between gap-2">
                      <span>{h.period}</span>
                      <span>{h.status}</span>
                      <button type="button" className="text-blue-600 underline" onClick={() => verPreview(h._id)}>
                        ver
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 text-sm border rounded-lg" onClick={() => setConfigCliente(null)}>
                Cerrar
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm bg-blue-700 text-white rounded-lg"
                onClick={guardarConfig}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
